import { PersonType } from '@/types/models';

export interface PersonDisplayFields {
	firstName?: string | null;
	lastName?: string | null;
	companyName?: string | null;
	personType?: PersonType | string | null;
}

/**
 * The one way to render a Person's name.
 *
 * A `company` person (a builder holding a spec home, a trust, a bank) keeps its
 * legal name in `companyName` and stores `firstName` as `''` — the column is
 * NOT NULL and the table can't be rebuilt, see migration 236. Joining
 * first + last on such a row yields an empty string, which is how a
 * company-owned account ends up rendering a blank name.
 *
 * Falls back to the company name for ANY person with no personal name, not just
 * those explicitly typed 'company', so rows created before #424 (and any
 * imported company-only owner) still display something.
 */
export const personDisplayName = (p: PersonDisplayFields | null | undefined): string => {
	if (!p) return '';
	const company = (p.companyName ?? '').trim();
	if (p.personType === PersonType.Company) return company;
	const personal = [p.firstName, p.lastName]
		.map((s) => (s ?? '').trim())
		.filter(Boolean)
		.join(' ');
	return personal || company;
};

/** True when this person has no personal name and is identified by its company. */
export const isCompanyPerson = (p: PersonDisplayFields | null | undefined): boolean => {
	if (!p) return false;
	if (p.personType === PersonType.Company) return true;
	const personal = [p.firstName, p.lastName].map((s) => (s ?? '').trim()).filter(Boolean);
	return personal.length === 0 && (p.companyName ?? '').trim().length > 0;
};
